import SvgIcon from "@/components/common/SvgIcon";
import {
  Avatar,
  Button,
  ButtonsMenu,
  TButtonsMenu,
  Typography,
  UnderlineTabs,
} from "@/components/kit";
import React, { ReactNode } from "react";

// Types
interface Tab {
  title: ReactNode;
  disable?: boolean;
  isAccessible?: boolean | null;
  icon?: string;
  value: string;
  className?: string;
}

interface DetailPageLayoutProps {
  // Header props
  iconName?: string;
  src?: string;
  title: string;
  description?: ReactNode;
  actionMenuItems?: TButtonsMenu["lists"];

  // Tab props
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;

  // Content
  children: ReactNode;

  // Bottom actions
  backText?: ReactNode;
  onBack?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  showSaveActions?: boolean;
  isLoading?: boolean;
  isDirty?: boolean;
  isDisabledCancel?: boolean;
  hasActionBar?: boolean;
  button?: ReactNode;
}

const DetailPageLayout: React.FC<DetailPageLayoutProps> = ({
  // Header props
  iconName,
  src,
  title,
  description,
  actionMenuItems = [],

  // Tab props
  tabs,
  activeTab,
  onTabChange,

  // Content
  children,

  // Bottom actions
  backText = "Back",
  onBack,
  onCancel,
  onSave,
  showSaveActions = true,
  isLoading,
  isDirty = true,
  isDisabledCancel = false,
  hasActionBar,
  button,
}) => {
  return (
    <div className=" flex pt-5 h-full flex-col">
      {/* Header Section */}
      <div className="flex flex-col">
        <div className="flex flex-col px-6 gap-4">
          {/* Title and Actions Row */}
          <div className="flex gap-4 items-center">
            {!src && !iconName && <Avatar size="xl" />}
            {src && (
              <div className=" bg-gra flex items-center justify-center">
                <img className="size-16 object-cover " src={src} alt="" />
              </div>
            )}
            {/* Icon */}
            {iconName && (
              <div className="w-16 h-16 bg-blue-100 flex items-center justify-center rounded-full">
                <SvgIcon
                  className="w-8 h-8"
                  fill="fill-gray-800"
                  iconName={iconName}
                />
              </div>
            )}

            {/* Title and Description */}
            <div className="flex flex-col gap-2  flex-1">
              <Typography variant="Title3-Bold">{title}</Typography>
              {description && (
                <Typography variant="Body3-Regular">{description}</Typography>
              )}
            </div>

            <div className="" />

            {/* Actions Menu */}

            <div className="flex items-center gap-3">
              {actionMenuItems.length > 0 && (
                <ButtonsMenu
                  button={
                    <Button size="md" variant="outlined">
                      Actions
                      <SvgIcon
                        iconName="arrow"
                        className="w-4 h-4"
                        fill="fill-black"
                      />
                    </Button>
                  }
                  buttonTitle="Actions"
                  lists={actionMenuItems}
                />
              )}

              {button}
            </div>
          </div>

          {/* Tabs */}
          <UnderlineTabs
            list={tabs}
            activeTab={activeTab}
            setActiveTab={onTabChange}
            headerProps={{ className: "border-b border-gray-100 !mb-0 !px-0" }}
          />
        </div>
      </div>

      {/* Content Area */}
      <div className=" overflow-auto py-5 flex-1 h-full content-start w-full">
        {children}
      </div>

      {/* Bottom Action Bar */}
      {hasActionBar && (
        <div
          className="ps-6 pe-20 py-3 w-full flex gap-4"
          style={{ boxShadow: "0px -2px 8px 0px rgba(0, 0, 0, 0.06)" }}
        >
          <Button variant="text" color="blue" onClick={onBack}>
            {backText}
          </Button>

          <div className="flex-1" />

          {showSaveActions && (
            <>
              <Button
                disabled={isDisabledCancel}
                variant="outlined"
                onClick={onCancel}
              >
                Discard
              </Button>
              <Button
                type="submit"
                disabled={!isDirty}
                isLoading={isLoading}
                onClick={onSave}
              >
                Save changes
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DetailPageLayout;
